/**
 * @file element-grid.ts
 * @description Сетки и оформление элементов секции — этап 3б партии SB120
 * «элементы» (phase19.16; site-builder.md SB120, SB10, SB24; проект данных
 * docs/phases/phase19.16-materials/db-design-sb120-stage3.md §1 «В 3б
 * остаётся чисто добавочным», §9; проект UI uiux-sb120-stage3.md §3
 * «Плитки и категории», «Сетки остальных видов», «Вопросы и ответы»,
 * «Фото товара», §5, §7).
 *
 * Здесь один источник (SB10) для схемы (schema.ts), витрины и кабинета:
 *   - допустимые наборы «в ряд» (1–6 у сеток, 1–8 у плиток и категорий,
 *     1–4 у отзывов) и раскладок на телефоне, значки вопросов, раскладки
 *     фото товара — с подписями кабинета;
 *   - чистые помощники: плиток в ряд на планшете (`tabletColsFor`),
 *     затемнение плиток по набору (`tileShadeDefault`, `tileShadeOf`) и его
 *     градиент (`tileShadeGradient`), отзывы на ПК и планшете
 *     (`reviewStripWidth`, `reviewStripTabletWidth`, `reviewsTabletCols`),
 *     толщины линий таблицы (`tableLineWidths`).
 *
 * Всё добавочно, без версии секции: нет ключа — как было. Наборы — `as const`
 * литералами (zod 4 принимает массив в `z.literal`), чтобы витрина могла
 * держать исчерпывающие таблицы классов Tailwind по значению.
 */

import { SECTION_LOOK_LIMITS } from "./look-limits.js";

const LIM = SECTION_LOOK_LIMITS;

// ── «В ряд на ПК» ──

/** «В ряд на ПК» у галереи, товаров, объявлений-карточек, статей, цифр и похожих товаров: целые 1–6. */
export const GRID_PER_ROW = [1, 2, 3, 4, 5, 6] as const;
export type GridPerRow = (typeof GRID_PER_ROW)[number];

/** «В ряд на ПК» у плиток и категорий: целые 1–8. */
export const TILE_GRID_COLS = [1, 2, 3, 4, 5, 6, 7, 8] as const;
export type TileGridCols = (typeof TILE_GRID_COLS)[number];

/** «В ряд» у отзывов: 1–4; нет ключа — 3 (`SECTION_LOOK_LIMITS.reviewsPerRow.default`). */
export const REVIEW_PER_ROW = [1, 2, 3, 4] as const;
export type ReviewPerRow = (typeof REVIEW_PER_ROW)[number];

/** Подсказки «в ряд» у плиток и категорий (uiux-sb120-stage3 §3): [2][3][4][6]. */
export const TILE_COLS_HINTS = [2, 3, 4, 6] as const satisfies readonly TileGridCols[];

// ── На телефоне ──

/**
 * Раскладка сетки на телефоне: stack — стопкой в одну колонку, grid2 — по
 * две в ряд, scroll — лентой вбок. Один набор у галереи, товаров,
 * объявлений-карточек, статей (и у плиток, категорий, похожих товаров — там
 * прежние константы с теми же значениями).
 */
export const PHONE_GRID_LAYOUTS = ["stack", "grid2", "scroll"] as const;
export type PhoneGridLayout = (typeof PHONE_GRID_LAYOUTS)[number];

/** Подписи раскладок телефона — одни на все панели кабинета. */
export const PHONE_GRID_LAYOUT_LABELS: Readonly<Record<PhoneGridLayout, string>> = {
  stack: "Стопкой",
  grid2: "По две",
  scroll: "Лентой",
};

// ── Вопросы и ответы ──

/** Значок вопроса: plus — «+» с поворотом при раскрытии (как было), chevron — стрелка, none — без значка. */
export const FAQ_ICONS = ["plus", "chevron", "none"] as const;
export type FaqIcon = (typeof FAQ_ICONS)[number];

/** Подписи значков вопроса. */
export const FAQ_ICON_LABELS: Readonly<Record<FaqIcon, string>> = {
  plus: "Плюс с поворотом",
  chevron: "Стрелка",
  none: "Без значка",
};

// ── Фото товара ──

/**
 * Раскладка фото товара. `thumbs-side` — миниатюры слева: значение не
 * переименовывается (db-design-sb120-stage3 §1), «слева» — только подпись.
 * `thumbs-right` — этап 3б.
 */
export const PRODUCT_GALLERY_LAYOUTS = ["thumbs-below", "thumbs-side", "thumbs-right", "grid"] as const;
export type ProductGalleryLayout = (typeof PRODUCT_GALLERY_LAYOUTS)[number];

/** Подписи раскладок фото товара (uiux-sb120-stage3 §3 «Фото товара»). */
export const PRODUCT_GALLERY_LAYOUT_LABELS: Readonly<Record<ProductGalleryLayout, string>> = {
  "thumbs-below": "Миниатюры снизу",
  "thumbs-side": "Слева",
  "thumbs-right": "Справа",
  grid: "Сеткой",
};

// ── Помощники ──

/** Целое в пределах {min, max}; не число — min. */
function clampInt(value: number, range: { readonly min: number; readonly max: number }): number {
  if (!Number.isFinite(value)) return range.min;
  return Math.min(range.max, Math.max(range.min, Math.round(value)));
}

/** Сетка с правилом планшета: плитки или категории. */
export type TileGridKind = "tiles" | "categories";

/**
 * Плиток в ряд на планшете (768–1279) по «в ряд на ПК» (uiux-sb120-stage3
 * §3). Плитки: 1–3 — столько же, 4 → 2, 5–6 → 3 (как TileGrid), 7–8 → 4.
 * Категории: не больше 3 при любом числе (SB24 «планшет ≤3», решение
 * оркестратора; 4 → 3 — как CategoryTiles `CATEGORY_GRID` сейчас).
 */
export function tabletColsFor(cols: number, grid: TileGridKind = "tiles"): number {
  const n = clampInt(cols, LIM.tileCols);
  if (grid === "categories") return Math.min(n, 3);
  if (n <= 3) return n;
  if (n >= 7) return 4;
  return n === 4 ? 2 : 3;
}

/**
 * Затемнение фото плитки по умолчанию, % (нет ключа `shade`): светлый
 * набор — 45 (прежний градиент 45/10/35), тёмный — 0 (без затемнения).
 */
export function tileShadeDefault(tone: "light" | "dark"): number {
  return tone === "light" ? 45 : 0;
}

/** Затемнение плитки: своё число или умолчание набора. */
export function tileShadeOf(tile: { readonly tone: "light" | "dark"; readonly shade?: number }): number {
  return tile.shade ?? tileShadeDefault(tile.tone);
}

/** Градиент затемнения: непрозрачность сверху, в середине и снизу (0–1) и готовое значение CSS. */
export interface TileShadeGradient {
  top: number;
  middle: number;
  bottom: number;
  /** `linear-gradient(to bottom, …)` — для `background-image` слоя над фото. */
  css: string;
}

/** Форма прежнего градиента плиток (TileGrid: from-black/45 via-black/10 to-black/35), доли от верхней точки. */
const SHADE_SHAPE = { top: 45, middle: 10, bottom: 35 } as const;

const alpha = (value: number): number => Math.round(value * 10000) / 10000;

/**
 * Градиент затемнения по силе `shade` 0–80 %: форма 45/10/35 сохраняется,
 * масштабируется по верхней точке (45 → ровно прежний градиент). 0 и меньше
 * — null (слоя нет); больше 80 приводится к 80.
 */
export function tileShadeGradient(shade: number): TileShadeGradient | null {
  if (!Number.isFinite(shade) || shade <= 0) return null;
  const s = Math.min(LIM.tileShade.max, shade);
  const k = s / SHADE_SHAPE.top / 100;
  const top = alpha(SHADE_SHAPE.top * k);
  const middle = alpha(SHADE_SHAPE.middle * k);
  const bottom = alpha(SHADE_SHAPE.bottom * k);
  return {
    top,
    middle,
    bottom,
    css: `linear-gradient(to bottom, rgb(0 0 0 / ${top}) 0%, rgb(0 0 0 / ${middle}) 50%, rgb(0 0 0 / ${bottom}) 100%)`,
  };
}

/**
 * Отзывы по устройствам (ReviewsElement: лента `w-[85%] md:w-[48%] xl:w-[32%]`,
 * сетка `md:grid-cols-2 xl:grid-cols-3`): ПК — 96 / N (3 → 32, как было),
 * планшет — 48 (как было; при 1 в ряд — 96), телефон — 85. Нет ключа — 3.
 */

/** Ширина отзыва в ленте на телефоне, % (как было: `w-[85%]`). */
export const REVIEW_STRIP_PHONE_WIDTH = 85;

/** Ширина отзыва в ленте на ПК (с 1280), % — 96 / N: 1 → 96, 2 → 48, 3 → 32 (как было), 4 → 24. Вне 1–4 — к пределам. */
export function reviewStripWidth(perRow: number = LIM.reviewsPerRow.default): number {
  return 96 / clampInt(perRow, LIM.reviewsPerRow);
}

/** Отзывов в ряд на планшете (768–1279): не больше 2 — как `md:grid-cols-2` / `md:w-[48%]` сейчас. */
export function reviewsTabletCols(perRow: number = LIM.reviewsPerRow.default): number {
  return Math.min(clampInt(perRow, LIM.reviewsPerRow), 2);
}

/** Ширина отзыва в ленте на планшете, % — 96 / min(N, 2): 1 → 96, 2–4 → 48 (как было). */
export function reviewStripTabletWidth(perRow: number = LIM.reviewsPerRow.default): number {
  return 96 / reviewsTabletCols(perRow);
}

/** Толщины линий таблицы, px: между строками и под шапкой. */
export interface TableLineWidths {
  row: number;
  head: number;
}

/**
 * Толщины линий таблицы (один источник для витрины и кабинета, SB10).
 * Нет `lines` — как было: 1 px между строками, 2 px под шапкой. `width: 0` —
 * без линий (0 / 0). Иначе шапка на 1 px толще строк: `{width, width + 1}`,
 * под шапкой не больше 5 px (`tableLines.max` + 1).
 */
export function tableLineWidths(lines?: { readonly width: number }): TableLineWidths {
  if (!lines) return { row: LIM.tableLines.default, head: LIM.tableLines.head };
  const row = clampInt(lines.width, LIM.tableLines);
  if (row === 0) return { row: 0, head: 0 };
  return { row, head: Math.min(row + 1, LIM.tableLines.max + 1) };
}
